import { useEffect, useRef } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { EditableQuestion } from '../../../../../../types';

/**
 * The switch is the only thing that puts answer_selection on a question, so its value is
 * written from the switch rather than edited directly. Mirrors useInputFieldResetter,
 * except that turning the switch off writes 'displayed' rather than clearing the field:
 * form data carries a cleared value to the api as the string "null", and 'displayed' is
 * a real value saying exactly what the absent field says.
 *
 * A question that has never hidden its selection keeps no field at all — that absence is
 * what every client predating this reads, so it is not written over on the way past.
 */
export const useAnswerSelectionField = (
  isHidden: boolean,
  watch: UseFormWatch<EditableQuestion>,
  setValue: UseFormSetValue<EditableQuestion>
) => {
  const answerSelection = watch('answer_selection');
  const required = watch('required');

  // What `required` was before hiding forced it off, so turning the switch back off can
  // give it back. An author who toggles the switch to see what it does would otherwise
  // quietly downgrade a required question, with the switch re-enabled and nothing saying
  // to re-check it. Held in a ref because nothing should re-render when it changes.
  const requiredBeforeHiding = useRef<boolean | null>(null);

  useEffect(() => {
    if (isHidden) {
      setValue('answer_selection', 'hidden');

      if (requiredBeforeHiding.current === null) {
        requiredBeforeHiding.current = required;
      }
      // A client that hides the selection collects nothing for the question, so a required
      // one could never be satisfied and the patient would be stuck on the screen.
      setValue('required', false);
      return;
    }

    if (answerSelection === 'hidden') {
      setValue('answer_selection', 'displayed');
    }

    if (requiredBeforeHiding.current !== null) {
      setValue('required', requiredBeforeHiding.current);
      requiredBeforeHiding.current = null;
    }
  }, [isHidden, answerSelection, required, setValue]);
};
